import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, ShieldCheck, Waves, Wind } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/36884223/pexels-photo-36884223.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400";

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 100]);
  const yBadge = useTransform(scrollY, [0, 700], [0, 40]);

  return (
    <section className="relative overflow-hidden pb-24 pt-32 sm:pt-40 lg:pb-32">
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-orange-500/15 blur-[140px]"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8 inline-flex items-center gap-3 border border-white/10 bg-white/5 px-4 py-2 backdrop-blur"
            data-testid="hero-weather-badge"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-orange-500" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300">
              Built for coastal Taranaki weather
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-heading text-4xl font-extrabold uppercase leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Reliable roof &amp;</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="text-outline">gutter solutions</span>
            </MaskedLine>
            <MaskedLine delay={0.41}>
              across <span className="text-orange-500">Taranaki</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            From gutter cleans and repairs to full roof paints and replacements — owner-operator
            Jason keeps local homes watertight, whatever the coast throws at them.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center gap-2 bg-orange-500 px-7 py-4 font-heading text-sm font-bold uppercase tracking-[0.15em] text-[#0B0F17] transition-colors duration-300 hover:bg-orange-400"
            >
              Request a free quote
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
            <a
              href="tel:0225477534"
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-3 border border-white/15 px-7 py-4 font-mono text-sm uppercase tracking-[0.15em] text-slate-200 transition-colors duration-300 hover:border-orange-500/60 hover:text-orange-400"
            >
              <Phone className="h-4 w-4" />
              022 547 7534
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500"
          >
            <span>Owner-operated</span>
            <span>All of Taranaki</span>
            <span>Free no-pressure quotes</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yImg }} className="clip-corner relative overflow-hidden border border-white/10">
              <img
                src={HERO_IMG}
                alt="Roofer at work on a Taranaki home"
                className="h-[24rem] w-full object-cover sm:h-[30rem]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/70 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              style={{ y: yBadge }}
              className="absolute -bottom-6 -left-4 border border-white/10 bg-[#0B0F17]/85 p-5 backdrop-blur-xl sm:-left-8"
              data-testid="hero-weather-card"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-400">Coastal-rated</p>
              <div className="mt-3 flex items-center gap-5 text-slate-300">
                <span className="flex items-center gap-2 text-xs">
                  <Wind className="h-4 w-4 text-orange-400" /> Wind
                </span>
                <span className="flex items-center gap-2 text-xs">
                  <Waves className="h-4 w-4 text-orange-400" /> Salt air
                </span>
                <span className="flex items-center gap-2 text-xs">
                  <ShieldCheck className="h-4 w-4 text-orange-400" /> Rain
                </span>
              </div>
            </motion.div>

            <span className="absolute -right-2 -top-2 h-6 w-6 border-r-2 border-t-2 border-orange-500" aria-hidden="true" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
